import React from 'react';
import { cn } from '../../../utils/cn';

const CalendarGrid = ({ 
  currentDate, 
  currentView, 
  events, 
  onEventClick, 
  onDateClick,
  selectedCalendar 
}) => {
  
  const formatTime = (timeString) => {
    return new Date(timeString)?.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventColor = (eventType, status) => {
    const colorMap = {
      'atendimento': {
        bg: 'bg-primary/10 hover:bg-primary/20',
        border: 'border-primary/30',
        text: 'text-primary'
      },
      'disponivel': {
        bg: 'bg-success/10 hover:bg-success/20',
        border: 'border-success/30',
        text: 'text-success'
      },
      'bloqueado': {
        bg: 'bg-error/10 hover:bg-error/20',
        border: 'border-error/30',
        text: 'text-primary'
      },
      'evento': {
        bg: 'bg-secondary/10 hover:bg-secondary/20',
        border: 'border-secondary/30',
        text: 'text-secondary'
      }
    };

    return colorMap?.[eventType] || colorMap?.['atendimento'];
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'confirmed': '✅',
      'pending': '⏳',
      'available': '🔓',
      'busy': '🚫'
    };
    return iconMap?.[status] || '';
  };

  const renderMonthView = () => {
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate?.setDate(startDate?.getDate() - firstDay?.getDay());

    const days = [];
    const currentDateObj = new Date(startDate);

    // Generate 6 weeks (42 days)
    for (let i = 0; i < 42; i++) {
      const dateStr = currentDateObj?.toISOString()?.split('T')?.[0];
      const dayEvents = events?.filter(event => 
        event?.startTime?.startsWith(dateStr)
      ) || [];

      days?.push({
        date: new Date(currentDateObj),
        dateStr,
        events: dayEvents,
        isCurrentMonth: currentDateObj?.getMonth() === month,
        isToday: currentDateObj?.toDateString() === new Date()?.toDateString()
      });

      currentDateObj?.setDate(currentDateObj?.getDate() + 1);
    }

    return (
      <div className="flex flex-col h-full">
        {/* Days of week header */}
        <div className="grid grid-cols-7 border-b border-border bg-background/50">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']?.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="flex-1 grid grid-rows-6">
          {Array.from({ length: 6 }, (_, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b border-border last:border-b-0">
              {days?.slice(weekIndex * 7, (weekIndex + 1) * 7)?.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={cn(
                    "border-r border-border last:border-r-0 p-2 min-h-[100px] cursor-pointer hover:bg-muted/30 transition-colors",
                    !day?.isCurrentMonth && "bg-muted/20 text-muted-foreground",
                    day?.isToday && "bg-primary/5 border-primary/20"
                  )}
                  onClick={() => onDateClick?.(day?.date)}
                >
                  <div className={cn(
                    "text-sm font-medium mb-1",
                    day?.isToday && "text-primary"
                  )}>
                    {day?.date?.getDate()}
                  </div>
                  
                  <div className="space-y-1">
                    {day?.events?.slice(0, 3)?.map((event) => {
                      const colors = getEventColor(event?.type, event?.status);
                      return (
                        <div
                          key={event?.id}
                          className={cn(
                            "text-xs p-1 rounded border cursor-pointer transition-colors",
                            colors?.bg,
                            colors?.border,
                            colors?.text
                          )}
                          onClick={(e) => {
                            e?.stopPropagation();
                            onEventClick?.(event);
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{getStatusIcon(event?.status)}</span>
                            <span className="truncate">
                              {formatTime(event?.startTime)} {event?.title}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {day?.events?.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{day?.events?.length - 3} mais
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek?.setDate(currentDate?.getDate() - currentDate?.getDay());

    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date?.setDate(startOfWeek?.getDate() + i);
      return date;
    });

    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="flex flex-col h-full">
        {/* Days header */}
        <div className="grid grid-cols-8 border-b border-border bg-background/50">
          <div className="p-3 border-r border-border"></div>
          {days?.map(day => (
            <div key={day?.toISOString()} className="p-3 text-center border-r border-border last:border-r-0">
              <div className="text-xs text-muted-foreground">
                {day?.toLocaleDateString('pt-BR', { weekday: 'short' })}
              </div>
              <div className={cn(
                "text-lg font-medium",
                day?.toDateString() === new Date()?.toDateString() && "text-primary"
              )}>
                {day?.getDate()}
              </div>
            </div>
          ))}
        </div>
        {/* Time grid */}
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-8 min-h-full">
            {/* Time column */}
            <div className="border-r border-border bg-background/50">
              {hours?.map(hour => (
                <div key={hour} className="h-16 border-b border-border p-2 text-xs text-muted-foreground">
                  {hour?.toString()?.padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days?.map(day => {
              const dayStr = day?.toISOString()?.split('T')?.[0];
              const dayEvents = events?.filter(event => 
                event?.startTime?.startsWith(dayStr)
              ) || [];

              return (
                <div key={day?.toISOString()} className="border-r border-border last:border-r-0 relative">
                  {hours?.map(hour => (
                    <div 
                      key={hour} 
                      className="h-16 border-b border-border hover:bg-muted/20 cursor-pointer transition-colors"
                      onClick={() => onDateClick?.(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour))}
                    />
                  ))}
                  {/* Events overlay */}
                  {dayEvents?.map(event => {
                    const startTime = new Date(event.startTime);
                    const endTime = new Date(event.endTime);
                    const startMinutes = startTime?.getHours() * 60 + startTime?.getMinutes();
                    const duration = (endTime?.getTime() - startTime?.getTime()) / (1000 * 60);
                    const top = (startMinutes / 60) * 64; // 64px per hour
                    const height = Math.max((duration / 60) * 64, 20);

                    const colors = getEventColor(event?.type, event?.status);

                    return (
                      <div
                        key={event?.id}
                        className={cn(
                          "absolute left-1 right-1 rounded border cursor-pointer transition-colors event-slot z-10",
                          colors?.bg,
                          colors?.border,
                          colors?.text
                        )}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`
                        }}
                        onClick={(e) => {
                          e?.stopPropagation();
                          onEventClick?.(event);
                        }}
                      >
                        <div className="p-1 text-xs overflow-hidden">
                          <div className="flex items-center space-x-1">
                            <span>{getStatusIcon(event?.status)}</span>
                            <span className="font-medium">{event?.title}</span>
                          </div>
                          <div className="text-xs opacity-75">
                            {formatTime(event?.startTime)} - {formatTime(event?.endTime)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      {currentView === 'month' ? renderMonthView() : renderWeekView()}
    </div>
  );
};

export default CalendarGrid;